// Layout Component
// LazySEM Frontend - Main application layout shell

import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useOrganization } from "../../contexts/OrganizationContext";
import { TopBar } from "../TopBar/TopBar";
import { SideNavigation } from "../SideNavigation/SideNavigation";
import { ContentArea } from "../ContentArea/ContentArea";
import { PendingInvitationsBanner } from "../PendingInvitationsBanner";
import { RequireOrganization } from "../RequireOrganization";

import { useBreakpoint } from "../../hooks/useBreakpoint";
import { useNavigation } from "../../hooks/useNavigation";
import CommandPalette from "../CommandPalette/CommandPalette";
import FloatingAgent from "../AgentChat/FloatingAgent";
import { AgentProvider } from "../../contexts/AgentContext";
import { WorkspaceQueryProvider } from "../../features/toolExperience/WorkspaceQueryProvider";
import styles from "./Layout.module.css";

interface LayoutProps {
  children?: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, isLoading } = useAuth();
  const { currentOrganization } = useOrganization();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile, isTablet /* isDesktop */ } = useBreakpoint(); // isDesktop not used
  const { isCollapsed, setIsCollapsed, activeRoute } = useNavigation();

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isLoading && !user) {
      navigate("/login", {
        state: { from: location },
        replace: true,
      });
    }
  }, [user, isLoading, navigate, location]);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [
    ,/* isSidebarOpen */
    /* setIsSidebarOpen */
  ] = useState(false); // Not used

  // Auto-collapse sidebar on mobile/tablet
  useEffect(() => {
    if (isMobile) {
      setIsCollapsed(true);
      setShowMobileMenu(false);
    } else if (isTablet) {
      setIsCollapsed(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, isTablet]);

  // Close mobile menu when route changes
  useEffect(() => {
    setShowMobileMenu(false);
  }, [activeRoute]);

  const handleToggleSidebar = () => {
    if (isMobile) {
      setShowMobileMenu(!showMobileMenu);
    } else {
      setIsCollapsed(!isCollapsed);
    }
  };

  const handleCloseMobileMenu = () => {
    setShowMobileMenu(false);
  };

  /* const toggleSidebar = () => { // Not used
    setIsSidebarOpen(!isSidebarOpen);
  }; */

  if (isLoading) {
    return (
      <div className={styles.loadingLayout}>
        <div className={styles.loadingSpinner} />
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) {
    // Render auth pages without layout
    return <div className={styles.authLayout}>{children || <Outlet />}</div>;
  }

  return (
    <WorkspaceQueryProvider key={`${user.id}:${currentOrganization?.id || ""}`}>
      <AgentProvider>
        <div
          className={`${styles.layout} ${isCollapsed ? styles.layoutCollapsed : ""}`}
        >
          {/* Mobile Menu Overlay */}
          {isMobile && showMobileMenu && (
            <div
              className={styles.mobileOverlay}
              onClick={handleCloseMobileMenu}
              aria-label="Close menu"
            />
          )}

          {/* Top Bar */}
          <TopBar
            user={user}
            onToggleSidebar={handleToggleSidebar}
            isMobile={isMobile}
            isCollapsed={isCollapsed}
            isMenuOpen={showMobileMenu}
            className={styles.topBar}
          />

          {/* Side Navigation */}
          <SideNavigation
            isCollapsed={isMobile ? false : isCollapsed}
            showMobileMenu={showMobileMenu}
            onToggle={handleToggleSidebar}
            onClose={handleCloseMobileMenu}
            activeRoute={activeRoute}
            isMobile={isMobile}
            className={styles.sideNavigation}
          />

          {/* Content Area */}
          <ContentArea className={styles.contentArea}>
            <PendingInvitationsBanner />
            <RequireOrganization>{children || <Outlet />}</RequireOrganization>
          </ContentArea>

          <CommandPalette />

          {/* Route-independent assistant panel shares the conversation and draft. */}
          <FloatingAgent />
        </div>
      </AgentProvider>
    </WorkspaceQueryProvider>
  );
};

export default Layout;
